// Globals
import debounce from 'lodash.debounce';
import { ChangeEvent, memo, ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// Store
import { useAppDispatch, useAppState } from '@/store';
import { changeQuery, getMetaTags, selectQuery } from '../../feature';

// Components
import { Input } from '@/ui';
import { StyledFilterRow } from './MetaTagsFiltersContainerStyles';

// Modules
import { Resource, usePermissions } from '@/modules/Permissions';

// Helpers
import { GetMetaTagsDTO } from '../../dtos';
import { MetaTagsFiltersFields } from '../../helpers/types';

type IMetaTagsFiltersContainerProps = {
  children?: ReactNode;
  className?: string;
};

const MetaTagsFiltersContainer = ({ children, className }: IMetaTagsFiltersContainerProps) => {
  const { t } = useTranslation(['metatags']);
  const dispatch = useAppDispatch();
  const query = useAppState(selectQuery);
  const { canManage } = usePermissions();
  // The trailing actions column only exists when the admin can manage SEO meta tags.
  const canManageMetaTags = canManage(Resource.SEO);

  const handleDebounceSearch = useMemo(
    () =>
      debounce(async (searchParams) => {
        const dto = new GetMetaTagsDTO(searchParams);
        dispatch(getMetaTags(dto));
      }, 500),
    [dispatch],
  );

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const newQuery = { ...query, [name]: value || undefined };
    dispatch(changeQuery(newQuery));
    handleDebounceSearch(newQuery);
  };

  return (
    <thead className={className}>
      {children}
      <StyledFilterRow>
        <th>
          <Input
            placeholder={t('metatags_slug_label')}
            name={MetaTagsFiltersFields.SLUG}
            value={query?.[MetaTagsFiltersFields.SLUG]}
            onChange={handleSearch}
            size="large"
          />
        </th>
        <th />
        {canManageMetaTags && <th />}
      </StyledFilterRow>
    </thead>
  );
};

export default memo(MetaTagsFiltersContainer);
